import { alpha } from '@mui/material/styles';
/** A number because MRT and a row's `height` take px. */
export const TABLE_HEAD_HEIGHT_PX = 38;
/** A number because MRT and a row's `height` take px. A section row is a minimum: rich content grows it. */
export const TABLE_ROW_HEIGHT_PX = { screen: 44, section: 52 };
/** Inline padding of a cell; for a section row it is the row's own inset from the card edge. */
export const TABLE_CELL_PADDING_X = {
    screen: '0.75rem',
    section: '1.125rem',
};
export const SECTION_TABLE_COLUMN_GAP = '0.875rem';
export const SECTION_TABLE_ROW_PADDING_Y = '0.375rem';
/** Space between the page's sticky header and a section table header stuck under it. */
export const SECTION_TABLE_STICKY_GAP = '0.75rem';
/**
 * Width of the table's container, not of the viewport, below which a section table hides its
 * header and each row lays its cells out by grid areas. A number for the `@container` query.
 */
export const SECTION_TABLE_STACK_BREAKPOINT_PX = 720;
export const SECTION_TABLE_STACKED_ROW_PADDING = `0.625rem ${SECTION_TABLE_COLUMN_GAP}`;
export const SECTION_TABLE_STACKED_ROW_GAP = '0.375rem';
/** The `·` between two sort keys in one header cell. */
export const SECTION_TABLE_SORT_SEPARATOR_STYLES = {
    margin: '0 0.375rem',
    opacity: 0.5,
};
const TABLE_VARIANT_LOOK = {
    screen: {
        headFontSize: '0.6875rem',
        bodyFontSize: '0.78125rem',
        dividerOpacity: 1,
        rowTransition: 'background-color 120ms ease',
        sortIconSize: '0.75rem',
        sortIconGap: '0.125rem',
        // A screen table draws an arrow only on the column it is sorted by.
        sortIconIdleOpacity: 0,
        sortIconActiveOpacity: 0.6,
    },
    section: {
        headFontSize: '0.71875rem',
        bodyFontSize: '0.8125rem',
        dividerOpacity: 0.5,
        rowTransition: 'none',
        sortIconSize: '0.8125rem',
        sortIconGap: '0.25rem',
        sortIconIdleOpacity: 0.55,
        sortIconActiveOpacity: 1,
    },
};
export const getTableHeadBackground = (theme, variant) => variant === 'screen'
    ? theme.palette.custom.OV.sys.surfaceContainerLowest
    : theme.palette.custom.OV.sys.surfaceContainerHigh;
export const getTableRowHoverBackground = (theme, variant) => variant === 'screen'
    ? theme.palette.custom.OV.sys.surfaceContainer
    : alpha(theme.palette.custom.OV.sys.onSurface, 0.04);
/**
 * The line under the header and between rows. A screen table draws it on every cell, as MUI
 * table cells do; a section table draws it on the row.
 */
export const getTableRowDivider = (theme, variant) => `1px solid ${alpha(theme.palette.custom.OV.sys.outlineVariant, TABLE_VARIANT_LOOK[variant].dividerOpacity)}`;
/** Header cells: typography and surface. The row takes `TABLE_HEAD_HEIGHT_PX`. */
export const getTableHeadStyles = (theme, variant) => ({
    backgroundColor: getTableHeadBackground(theme, variant),
    fontSize: TABLE_VARIANT_LOOK[variant].headFontSize,
    fontWeight: 600,
    lineHeight: 'normal',
    color: theme.palette.custom.OV.sys.onSurfaceVariant,
    whiteSpace: 'nowrap',
});
/** A body row: typography and hover. The row takes `TABLE_ROW_HEIGHT_PX[variant]`. */
export const getTableRowStyles = (theme, variant) => ({
    fontSize: TABLE_VARIANT_LOOK[variant].bodyFontSize,
    fontWeight: 400,
    color: theme.palette.custom.OV.sys.onBackground,
    transition: TABLE_VARIANT_LOOK[variant].rowTransition,
    '&:hover': { backgroundColor: getTableRowHoverBackground(theme, variant) },
});
export const getTableSortStyles = (theme, variant) => {
    const look = TABLE_VARIANT_LOOK[variant];
    return {
        icon: {
            flexShrink: 0,
            width: look.sortIconSize,
            height: look.sortIconSize,
            marginLeft: look.sortIconGap,
            opacity: look.sortIconIdleOpacity,
        },
        activeIcon: { opacity: look.sortIconActiveOpacity },
        labelHighlightColor: variant === 'screen'
            ? theme.palette.custom.OV.sys.onSurfaceVariant
            : theme.palette.custom.OV.sys.onBackground,
    };
};
/**
 * A section table header once it sticks under the page header. `::before` paints the page
 * background over the sticky gap and the card's top border, so rows scrolling past do not show
 * through above the header. It is a square pseudo-element rather than a shadow because a shadow
 * takes the header's rounded corners and leaves the card's side borders showing above them.
 */
export const getSectionTableStuckHeadStyles = (theme) => ({
    boxShadow: `0 0.375rem 0.75rem -0.5rem ${alpha(theme.palette.custom.OV.sys.shadow, 0.35)}`,
    '&::before': {
        content: '""',
        position: 'absolute',
        left: '-1px',
        right: '-1px',
        bottom: '100%',
        height: `calc(${SECTION_TABLE_STICKY_GAP} + 1px)`,
        backgroundColor: theme.palette.custom.OV.sys.background,
    },
});
const SECTION_TABLE_FOOTER_PADDING_Y = '0.8125rem';
const SECTION_TABLE_FOOTER_FONT_SIZE = '0.78125rem';
/** The line under the last row that leads to the rows not shown. */
export const getSectionTableFooterStyles = (theme) => ({
    padding: `${SECTION_TABLE_FOOTER_PADDING_Y} ${TABLE_CELL_PADDING_X.section}`,
    fontSize: SECTION_TABLE_FOOTER_FONT_SIZE,
    color: theme.palette.custom.OV.sys.onSurfaceVariant,
});
/** The message a section table shows in place of rows when it has none. */
export const getSectionTableEmptyStyles = (theme) => ({
    padding: TABLE_CELL_PADDING_X.section,
    fontSize: TABLE_VARIANT_LOOK.section.bodyFontSize,
    color: theme.palette.custom.OV.sys.onSurfaceVariant,
    textAlign: 'center',
});
/** The list surface a screen table's body sits on; its head is drawn in the same surface. */
export const getScreenTableSurfaceBackground = (theme) => theme.palette.custom.OV.sys.surfaceContainerLowest;
export const getScreenTableSelectedRowBackground = (theme, { hovered }) => alpha(theme.palette.custom.OV.sys.secondary, hovered ? 0.13 : 0.09);
/** A number because it is an MRT column `size`. */
export const SCREEN_TABLE_SELECT_COLUMN_WIDTH_PX = 34;
export const getScreenTableCheckboxStyles = (theme) => ({
    box: {
        width: '1rem',
        height: '1rem',
        boxSizing: 'border-box',
        border: `1.5px solid ${theme.palette.custom.OV.sys.outline}`,
        borderRadius: '0.25rem',
        backgroundColor: 'transparent',
        color: theme.palette.custom.OV.sys.onSecondary,
        transition: 'background-color 120ms, border-color 120ms',
    },
    checkedBox: {
        backgroundColor: theme.palette.custom.OV.sys.secondary,
        borderColor: theme.palette.custom.OV.sys.secondary,
    },
    focusRing: `0 0 0 0.125rem ${alpha(theme.palette.custom.OV.sys.secondary, 0.4)}`,
    glyphSize: '0.75rem',
    glyphStrokeWidth: 3,
    hiddenOpacity: 0,
    transition: 'opacity 120ms',
});
export const getScreenTableResizeHandleStyles = (theme) => ({
    hitArea: { position: 'absolute', top: 0, right: 0, width: '0.5625rem', height: '100%' },
    bar: {
        position: 'absolute',
        top: '0.5625rem',
        bottom: '0.5625rem',
        right: 0,
        width: '0.125rem',
        borderRadius: '0.125rem',
        backgroundColor: theme.palette.custom.OV.sys.outlineVariant,
        opacity: 0,
        transition: 'opacity 120ms, background-color 120ms',
    },
    barHover: { opacity: 1 },
    barDragging: { opacity: 1, backgroundColor: theme.palette.custom.OV.sys.secondary },
});
